import React, {useState, useCallback} from 'react';
import {useNavigate, useParams} from "react-router-dom";
import Box from '@mui/material/Box';
import Grid from "@mui/material/Grid";
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import Typography from "@mui/material/Typography";
import { useForm, SubmitHandler } from "react-hook-form";
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from "yup";

import {useAuth} from "../../_auth/useAuth";
import {useLocation} from "react-router-dom";
import {
    Form,
    DaButton,
    DaTextField,
    DaPasswordField,
    ProgressPanel,
    MessagePanel,
    IMessagePanelProps,
    ButtonBar,
} from "../_shared";
import {FormPaper} from "./PasswordReset.styles";

import {useTheme} from "@mui/material/styles";
import {confirmPasswordResetFields} from "../../_appData";
import {AuthConfirmPasswordResetOptions} from "../../_auth/types";

import {Location} from "history";
import {IPasswordResetConfirm} from "../../_api";

interface loginState {
    returnTo: Location
};

const resetSchema: yup.SchemaOf<IPasswordResetConfirm> = yup.object({
    email: yup.string().label(confirmPasswordResetFields.email.label).max(254).required(),
    code: yup.string().label(confirmPasswordResetFields.confirmationCode.label).max(254).required(),
    password: yup.string().label(confirmPasswordResetFields.password.label)
        .min(10, 'Password must be at least 10 characters long')
        .matches(/[a-z]/, 'Password must contain at least one lowercase letter')
        .matches(/[A-Z]/, 'Password must contain at least one uppercase letter')
        .matches(/\d/, 'Password must contain at least one number')
        .matches(/[@\$!%\*\?&#<>\^\+=\|]/, 'Password must contain at least one special character (@$!%*?&#<>^+=|)')
        .required('Password is required'),
    confirm_password: yup.string().label(confirmPasswordResetFields.confirmPassword.label)
        .oneOf([yup.ref("password"), null], "Passwords must match")
        .required(),
});

export const PasswordResetConfirmView: React.FC = () => {
    const theme = useTheme();
    const { passwordResetConfirm } = useAuth();
    const navigate = useNavigate();
    const params = useParams();
    const [msgPanelProps, setMsgPanelProps] = useState<IMessagePanelProps>({message: ""});
    const [progressTitle, setProgressTitle] = useState<string>("Processing Signup Info...");
    const [isShowError, setIsShowError] = useState<boolean>(false);
    const [showProgress, setShowProgress] = useState<boolean>(false);

    // legacy state values, need to refactor
    const { state } = useLocation();

    const formFieldSize = "medium";
    const formFieldVariant = "standard";
    const { email, } = state as AuthConfirmPasswordResetOptions; // Read values passed on state

    // defining the initial state for the form
    const initialValues = {

        email: email,
        password: '',
        confirm_password: '',
        code: '',
    };


    const { control, handleSubmit, formState: { errors } } = useForm<IPasswordResetConfirm>({
        defaultValues: initialValues,
        resolver: yupResolver(resetSchema)
    });

    const showError = ({title, message, type="error"}: IMessagePanelProps) => {
        const msgProps: IMessagePanelProps = {
            message: message,
            title: title,
            type: type
        }
        setMsgPanelProps(msgProps);
        setIsShowError(true);
    }

    // a submit function that will execute upon form submission
    const onSubmit: SubmitHandler<IPasswordResetConfirm> = (data) => {
        submitResetData(data);
    };

    const submitResetData = useCallback(async (options: AuthConfirmPasswordResetOptions): Promise<void> => {
        setProgressTitle("Performing Password Reset...");
        setShowProgress(true);

        let apiPromise = undefined;
        apiPromise = passwordResetConfirm(options);

        apiPromise && apiPromise.then((resp) => {
            if (resp && resp.state === "success"){
                navigate("/");
            } else {
                showError({
                    title: "Unable to Perform Password Reset ",
                    message: "An error occurred while saving Password Reset data. " +
                        "Verify that you have provided all required data and " +
                        "retry your last action."
                });
            }
        })
        .catch(() => {
            showError({
                title: "PasswordReset API Save Error",
                message: "An error occurred while saving Password Reset data. " +
                    "There may have been a network error.",
            });
        })
        .finally(() => {
            setShowProgress(false);
        });
    }, [passwordResetConfirm, navigate]);


    return (
        <>
            <FormPaper elevation={4}>
                {/* Form Title*/}
                {/*<FormTitle*/}
                {/*    title={"Sign Up"}*/}
                {/*    titleIcon={"account_circle_icon"}*/}
                {/*    typeVariant={"h4"}*/}
                {/*/>*/}
                <Box display="flex" flexDirection="column" alignItems="center">
                    <AccountCircleIcon
                        sx={{
                            fontSize: 68,
                            color: theme.palette.grey["500"],
                            mb: 2
                        }}
                    />
                    <Typography variant="h4">Password Reset</Typography>
                </Box>

                <Grid container>
                    {isShowError ? (
                        <Grid item xs={12} justifyContent={"start"}>
                            <MessagePanel
                                {...msgPanelProps}
                            />
                        </Grid>
                    ) : null}
                    {showProgress ? (
                        <Grid item xs={12}>
                            <ProgressPanel title={progressTitle}/>
                        </Grid>
                    ) : (
                        <Grid container item xs={12}>
                            <Form
                                autoComplete="off"
                                onSubmit={handleSubmit(onSubmit)}
                            >
                                <Grid container spacing={theme.spacing(1)}>
                                    <Grid item xs={12} md={12}>
                                        <DaTextField
                                            control={control}
                                            name={confirmPasswordResetFields.email.name}
                                            label={confirmPasswordResetFields.email.label}
                                            required={confirmPasswordResetFields.email.required}
                                            variant={formFieldVariant}
                                            size={formFieldSize}
                                            errors={errors}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={12}>
                                        <DaPasswordField
                                            control={control}
                                            name={confirmPasswordResetFields.confirmationCode.name}
                                            label={confirmPasswordResetFields.confirmationCode.label}
                                            required={confirmPasswordResetFields.confirmationCode.required}
                                            variant={formFieldVariant}
                                            size={formFieldSize}
                                            errors={errors}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={12}>
                                        <DaPasswordField
                                            control={control}
                                            name={confirmPasswordResetFields.password.name}
                                            label={confirmPasswordResetFields.password.label}
                                            required={confirmPasswordResetFields.password.required}
                                            variant={formFieldVariant}
                                            size={formFieldSize}
                                            errors={errors}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={12}>
                                        <DaPasswordField
                                            control={control}
                                            name={confirmPasswordResetFields.confirmPassword.name}
                                            label={confirmPasswordResetFields.confirmPassword.label}
                                            required={confirmPasswordResetFields.confirmPassword.required}
                                            variant={formFieldVariant}
                                            size={formFieldSize}
                                            errors={errors}
                                        />
                                    </Grid>
                                </Grid>
                                <Grid container spacing={theme.spacing(1)}>
                                    <Grid item xs={12} md={12}>
                                        <ButtonBar buttonAlignment="alignRight">
                                            <DaButton buttonStyle="primary" type="submit">
                                                Reset Password
                                            </DaButton>
                                        </ButtonBar>
                                    </Grid>
                                </Grid>
                            </Form>
                        </Grid>
                    )}
                </Grid>
            </FormPaper>
        </>
    );
}